import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Layer, LayerType } from '../types/layer';
import './Legend.css';

// EGIS 토지피복 세분류 [2025] 전국 분류 데이터 (다국어 지원)
const getEgisLandCoverTypes = (t: any) => [
  { id: 'urban', name: t('legend.urban'), color: '#ff0000', description: t('legend.urban') },
  { id: 'agricultural', name: t('legend.agricultural'), color: '#ffff00', description: t('legend.agricultural') },
  { id: 'forest', name: t('legend.forest'), color: '#00ff00', description: t('legend.forest') },
  { id: 'grassland', name: t('legend.grassland'), color: '#90EE90', description: t('legend.grassland') },
  { id: 'wetland', name: t('legend.wetland'), color: '#00ffff', description: t('legend.wetland') },
  { id: 'bareland', name: t('legend.bareland'), color: '#8B4513', description: t('legend.bareland') },
  { id: 'water', name: t('legend.water'), color: '#0000ff', description: t('legend.water') }
];

// 범례 컴포넌트 인터페이스
interface LegendProps {
  visible: boolean;
  layers: Layer[]; // 표시 중인 레이어들
  onEditLegend?: (layer: Layer) => void; // 범례 편집 함수
  onOpacityChange?: (layerId: string, opacity: number) => void; // 투명도 변경 함수

}

// 개별 레이어 범례 컴포넌트
interface LayerLegendProps {
  layer: Layer;
  onEditLegend?: (layer: Layer) => void;
  onOpacityChange?: (layerId: string, opacity: number) => void;

}

const LayerLegend: React.FC<LayerLegendProps> = ({ layer, onEditLegend, onOpacityChange }) => {
  const { t } = useTranslation();
  
  // 로컬 스토리지에서 개별 레이어의 펼침/접힘 상태 로드 (초기 접속 시 접힌 상태)
  const [expanded, setExpanded] = useState(() => {
    const savedState = localStorage.getItem(`legend-expanded-${layer.id}`);
    return savedState !== null ? JSON.parse(savedState) : false;
  });
  
  const [legendData, setLegendData] = useState<any[]>([]);

  // 레이어별 범례 데이터 로드
  useEffect(() => {
    const loadLegendData = async () => {
      // EGIS 레이어는 별도 처리
      if (layer.type === LayerType.EGIS) {
        setLegendData(getEgisLandCoverTypes(t));
        return;
      }
      
      // 1순위: 백엔드 API에서 최신 범례 데이터 가져오기 (캐시 무효화)
      try {
        const apiUrl = `/api/upload/layers/local/${layer.id}/legend?_t=${Date.now()}`;
        console.log('범례 API 호출:', apiUrl);
        const response = await fetch(apiUrl);
        
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data && result.data.items && result.data.items.length > 0) {
            console.log('API에서 범례 데이터 로드 성공:', result.data);
            const apiLegendData = result.data;
            const data = apiLegendData.items.map((item: any, index: number) => ({
              id: `${layer.id}-${index}`,
              name: item.label || `범주 ${item.value}`,
              color: item.color,
              value: item.value,
              opacity: item.opacity ?? 1,
              description: `값: ${item.value} - ${item.label || '분류'}`
            }));
            setLegendData(data);
            return;
          } else {
            console.log('API에서 범례 데이터가 비어있음:', result.data);
            // 범례 데이터가 없는 경우 빈 배열로 설정
            setLegendData([]);
            return;
          }
        }
      } catch (error) {
        console.error('범례 API 호출 실패:', error);
      }
      
      // 2순위: 저장된 범례 스타일이 있는 경우 사용
      if (layer.legendStyle?.items && layer.legendStyle.items.length > 0) {
        console.log('저장된 범례 스타일 사용:', layer.legendStyle);
        const data = layer.legendStyle.items.map((item: any, index: number) => ({
          id: `${layer.id}-${index}`,
          name: item.label || `범주 ${item.value}`,
          color: item.color,
          value: item.value,
          opacity: item.opacity ?? 1,
          description: `값: ${item.value} - ${item.label || '분류'}`
        }));
        setLegendData(data);
        return;
      }
      
      // 3순위: 사용자 정의 분류가 있는 경우
      if (layer.metadata?.classification?.categories && layer.metadata.classification.categories.length > 0) {
        const data = layer.metadata.classification.categories.map((category, index) => ({
          id: `${layer.id}-${index}`,
          name: category.name,
          color: category.color,
          description: category.description || '사용자 정의 분류'
        }));
        setLegendData(data);
        return;
      }
      
      // 범례 데이터가 없는 경우 빈 배열로 설정 (기본값 출력 방지)
      console.log('범례 데이터가 없음 - 빈 배열로 설정');
      setLegendData([]);
    };

    loadLegendData();
  }, [layer, layer.legendStyle, t]); // legendStyle 변경을 감지하도록 의존성 추가

  // 개별 레이어의 펼침/접힘 상태 변경 시 로컬 스토리지에 저장
  useEffect(() => {
    localStorage.setItem(`legend-expanded-${layer.id}`, JSON.stringify(expanded));
  }, [expanded, layer.id]);



  return (
    <div className="legend-layer">
      {/* 레이어 헤더 - 접기/펼치기와 편집 버튼 */}
      <div className="legend-layer-header">
        <div 
          className="legend-layer-title"
          onClick={() => setExpanded(!expanded)}
          style={{ cursor: 'pointer' }}
        >
          <span className="expand-icon">{expanded ? '−' : '+'}</span>
          <span className="legend-layer-name">{layer.name}</span>
        </div>
        <div className="legend-layer-actions">
          {layer.type === LayerType.EGIS && (
            <span className="legend-layer-source">
              🏛️ EGIS
            </span>
          )}
          {onEditLegend && (
            <button 
              className="legend-edit-btn"
              onClick={(e) => {
                e.stopPropagation();
                onEditLegend(layer);
              }}
              title={t('legend.editLegend') as string}
            >
              🎨
            </button>
          )}
        </div>
      </div>
      
      {expanded && (
        <div className="legend-content">
          {/* 투명도 조절 */}
          <div className="legend-opacity-control">
            <label htmlFor={`opacity-${layer.id}`}>{t('layers.opacity')}:</label>
            <input
              id={`opacity-${layer.id}`}
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={layer.opacity ?? 1.0}
              onChange={(e) => onOpacityChange?.(layer.id, parseFloat(e.target.value))}
              className="opacity-slider"
            />
            <span className="opacity-value">{Math.round((layer.opacity ?? 1.0) * 100)}%</span>
          </div>
          

          
          {/* 범례 내용 - EGIS는 항상 표시, 사용자 정의는 범례 데이터가 있을 때만 표시 */}
          {layer.type === LayerType.EGIS ? (
            <div className="legend-items">
              {legendData.map((type: any) => (
                <div key={type.id} className="legend-item" title={type.description}>
                  <div 
                    className="legend-color" 
                    style={{ backgroundColor: type.color }}
                  />
                  <span className="legend-label">
                    {type.name}
                    {type.value !== undefined && (
                      <span className="legend-value">({type.value})</span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          ) : legendData.length > 0 ? (
            <div className="legend-items">
              {legendData.map((type: any) => {
                const isTransparent = (type.opacity ?? 1) <= 0;
                return (
                <div key={type.id} className="legend-item" title={type.description}>
                  <div
                    className={`legend-color ${isTransparent ? 'legend-color-transparent' : ''}`}
                    style={isTransparent ? undefined : { backgroundColor: type.color }}
                  />
                  <span className="legend-label">
                    {type.name}
                    {type.value !== undefined && (
                      <span className="legend-value">({type.value})</span>
                    )}
                  </span>
                </div>
              )})}
            </div>
          ) : (
            <div className="legend-no-data">
              <span className="no-legend-text">범례 데이터가 없습니다</span>
            </div>
          )}
          

        </div>
      )}
    </div>
  );
};

const Legend: React.FC<LegendProps> = ({ visible, layers, onEditLegend, onOpacityChange }) => {
  const { t } = useTranslation();
  
  if (!visible || layers.length === 0) return null;

  // EGIS 레이어를 맨 위로 정렬
  const sortedLayers = [...layers].sort((a, b) => {
    // EGIS 레이어를 우선으로 정렬
    if (a.type === LayerType.EGIS && b.type !== LayerType.EGIS) return -1;
    if (a.type !== LayerType.EGIS && b.type === LayerType.EGIS) return 1;
    // 둘 다 EGIS이거나 둘 다 사용자 정의 레이어인 경우 기존 순서 유지
    return 0;
  });

  return (
    <div className="legend">
      <div className="legend-content-scroll">
        {sortedLayers.map((layer) => (
          <LayerLegend 
            key={layer.id} 
            layer={layer} 
            onEditLegend={onEditLegend}
            onOpacityChange={onOpacityChange}

          />
        ))}
      </div>
    </div>
  );
};

export default Legend; 